import {
  Body1,
  Button,
  Input,
  Select,
  Spinner,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Dismiss16Filled,
  Dismiss16Regular,
  Edit16Filled,
  Edit16Regular,
  Save16Filled,
  Save16Regular,
  Tag16Filled,
  Tag16Regular,
} from '@fluentui/react-icons';
import { useCallback, useState } from 'react';

import {
  DATASET_ROLE_CONTRIBUTOR,
  DATASET_ROLE_TOOLTIP_CONTRIBUTOR,
} from '../../../constants/index.js';
import { useAttributeDefinitions } from '../../../hooks/useAttributeDefinitions/useAttributeDefinitions.js';
import { useDatasetTags } from '../../../hooks/useDatasetTags/useDatasetTags.js';
import { hasDatasetRole } from '../../../hooks/useMyDatasetRole/hasDatasetRole.js';
import type { AttributeDefinition } from '../../../services/dataHubService.interface.js';

import type { DatasetAttributesTabProps } from './DatasetAttributesTab.interface.js';
import { useStyles } from './DatasetAttributesTab.style.js';

const EditIcon = bundleIcon(Edit16Filled, Edit16Regular) as React.FC;
const SaveIcon = bundleIcon(Save16Filled, Save16Regular) as React.FC;
const DismissIcon = bundleIcon(Dismiss16Filled, Dismiss16Regular) as React.FC;
const TagIcon = bundleIcon(Tag16Filled, Tag16Regular) as React.FC;

const VALUE_SELECT_PLACEHOLDER = '— select —';
const ENUM_TYPE_LABEL = 'enum';
const STRING_TYPE_LABEL = 'string';

function getTypeBadge(def: AttributeDefinition): string {
  if (def.valueType) return def.valueType;
  return def.allowedValues?.length ? ENUM_TYPE_LABEL : STRING_TYPE_LABEL;
}

function isEnumAttribute(def: AttributeDefinition): boolean {
  return (def.allowedValues?.length ?? 0) > 0;
}

// ── View mode ─────────────────────────────────────────────────────────────────

interface ViewModeProps {
  attributeDefinitions: AttributeDefinition[];
  tagsByDefId: Record<string, string>;
  onEdit: () => void;
  canEdit: boolean;
}

function ViewMode({
  attributeDefinitions,
  tagsByDefId,
  onEdit,
  canEdit,
}: ViewModeProps) {
  const styles = useStyles();

  const setTags = attributeDefinitions.filter(
    (def) => tagsByDefId[def.attributeDefinitionId] !== undefined,
  );

  return (
    <>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          <TagIcon />
          Attributes
        </span>
        {canEdit ? (
          <Button
            appearance="secondary"
            size="small"
            icon={<EditIcon />}
            onClick={onEdit}
          >
            Edit Attributes
          </Button>
        ) : (
          <Tooltip
            content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
            relationship="description"
          >
            <Button
              appearance="secondary"
              size="small"
              icon={<EditIcon />}
              disabledFocusable
            >
              Edit Attributes
            </Button>
          </Tooltip>
        )}
      </div>

      {setTags.length === 0 ? (
        <Body1 className={styles.emptyState}>No attributes set.</Body1>
      ) : (
        <div className={styles.attributeList}>
          {setTags.map((def) => (
            <div
              key={def.attributeDefinitionId}
              className={styles.attributeItem}
            >
              <span className={styles.attributeLabel}>
                {def.displayName ?? def.name}
                <span className={styles.typeBadge}>({getTypeBadge(def)})</span>
              </span>
              <span className={styles.attributeValue}>
                {tagsByDefId[def.attributeDefinitionId]}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── Edit mode ─────────────────────────────────────────────────────────────────

interface EditModeProps {
  attributeDefinitions: AttributeDefinition[];
  draftValues: Record<string, string>;
  isSaving: boolean;
  saveError: string | null;
  onValueChange: (attributeDefinitionId: string, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

function EditMode({
  attributeDefinitions,
  draftValues,
  isSaving,
  saveError,
  onValueChange,
  onSave,
  onCancel,
}: EditModeProps) {
  const styles = useStyles();

  return (
    <>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          <TagIcon />
          Attributes
        </span>
      </div>

      <div className={styles.attributeList}>
        {attributeDefinitions.map((def) => {
          const currentValue = draftValues[def.attributeDefinitionId] ?? '';
          const isEnum = isEnumAttribute(def);

          return (
            <div
              key={def.attributeDefinitionId}
              className={styles.attributeItem}
            >
              <span className={styles.attributeLabel}>
                {def.displayName ?? def.name}
                <span className={styles.typeBadge}>({getTypeBadge(def)})</span>
              </span>

              {isEnum ? (
                <Select
                  className={styles.attributeInput}
                  value={currentValue}
                  onChange={(_, data) =>
                    onValueChange(def.attributeDefinitionId, data.value)
                  }
                  aria-label={def.displayName ?? def.name}
                >
                  <option value="">{VALUE_SELECT_PLACEHOLDER}</option>
                  {def.allowedValues!.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  className={styles.attributeInput}
                  value={currentValue}
                  placeholder="Enter value"
                  onChange={(_, data) =>
                    onValueChange(def.attributeDefinitionId, data.value)
                  }
                  aria-label={def.displayName ?? def.name}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="small"
          icon={isSaving ? <Spinner size="tiny" /> : <SaveIcon />}
          onClick={onSave}
          disabled={isSaving}
        >
          Save
        </Button>
        <Button
          appearance="secondary"
          size="small"
          icon={<DismissIcon />}
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        {saveError !== null && (
          <span className={styles.saveError}>{saveError}</span>
        )}
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function DatasetAttributesTab({
  dataSetId,
  myRoles,
}: DatasetAttributesTabProps) {
  const styles = useStyles();
  const canEdit = hasDatasetRole(myRoles, DATASET_ROLE_CONTRIBUTOR);

  const [isEditing, setIsEditing] = useState(false);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    attributeDefinitions,
    isLoading: isLoadingDefs,
    error: defsError,
  } = useAttributeDefinitions();

  const {
    tags,
    isLoading: isLoadingTags,
    error: tagsError,
    saveTags,
  } = useDatasetTags(dataSetId);

  const tagsByDefId: Record<string, string> = {};
  for (const tag of tags) {
    if (tag.values.length > 0) {
      tagsByDefId[tag.attributeDefinitionId] = tag.values[0]!;
    }
  }

  const handleEditClick = useCallback(() => {
    const initial: Record<string, string> = {};
    for (const tag of tags) {
      if (tag.values.length > 0) {
        initial[tag.attributeDefinitionId] = tag.values[0]!;
      }
    }
    setDraftValues(initial);
    setSaveError(null);
    setIsEditing(true);
  }, [tags]);

  const handleValueChange = useCallback(
    (attributeDefinitionId: string, value: string) => {
      setDraftValues((prev) => ({ ...prev, [attributeDefinitionId]: value }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);

    const tagInputs = attributeDefinitions
      .map((def) => ({
        attributeDefinitionId: def.attributeDefinitionId,
        values: [draftValues[def.attributeDefinitionId] ?? ''].filter(
          (v) => v !== '',
        ),
      }))
      .filter((t) => t.values.length > 0);

    try {
      await saveTags({ tags: tagInputs });
      setIsEditing(false);
      setDraftValues({});
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save attributes',
      );
    } finally {
      setIsSaving(false);
    }
  }, [attributeDefinitions, draftValues, saveTags]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setDraftValues({});
    setSaveError(null);
  }, []);

  if (isLoadingDefs || isLoadingTags) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading attributes..." />
      </div>
    );
  }

  const fetchError = defsError ?? tagsError;
  if (fetchError !== null) {
    return (
      <Body1 className={styles.errorState}>
        Failed to load attributes: {fetchError}
      </Body1>
    );
  }

  return (
    <div className={styles.root}>
      {isEditing ? (
        <EditMode
          attributeDefinitions={attributeDefinitions}
          draftValues={draftValues}
          isSaving={isSaving}
          saveError={saveError}
          onValueChange={handleValueChange}
          onSave={() => void handleSave()}
          onCancel={handleCancel}
        />
      ) : (
        <ViewMode
          attributeDefinitions={attributeDefinitions}
          tagsByDefId={tagsByDefId}
          onEdit={handleEditClick}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
